'use client';
import { useUserContext } from '@/contexts/User';
import { useWalletConnection } from '@/hooks';
import { clearAuth } from '@/libs/auth';
import { disconnectHandler } from '@/utils';
import { ConnectKitButton } from 'connectkit';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useDisconnect } from 'wagmi';
import Card, { CardState } from './components/Card';

const items = [
  {
    emoji: '💼',
    title: 'Backed by Leading Web3 Investors',
    description:
      'MaxLead is trusted by top-tier decentralized platforms to deliver silent, integrated growth. It empowers protocols to scale without needing to expose or depend on centralized marketing layers.',
  },
  {
    emoji: '🌐',
    title: 'Live on IPFS with ENS Support',
    description:
      'MaxLead’s infrastructure is designed for full decentralization. It integrates natively into partner platforms while maintaining their sovereignty — just like IPFS and ENS preserve content and identity ownership.',
  },
  {
    emoji: '🛡️',
    title: 'Audited at the Core',
    description:
      'MaxLead connects through audited infrastructure used by leaders like OpenZeppelin and Trail of Bits. All wallet sessions are encrypted, non-custodial, and require no personal data — just connect and go.',
  },
  {
    emoji: '🧩',
    title: 'Built on MaxLead’s Core Framework',
    description:
      'MaxLead’s proprietary framework is built for scalability and speed. It delivers plug-and-play automation directly into platforms — no setup, no disruption, just native campaign execution at scale.',
  },
  {
    emoji: '🌟',
    title: 'Now Powered by Chainlink Feeds',
    description:
      'Real-time data from Chainlink feeds powers MaxLead’s smart growth engine — enabling verifiable campaign triggers, analytics, and adaptive targeting strategies across DeFi and Web3.',
  },
  {
    icon: '/images/logo/0x.svg',
    title: '0xarbitrage.org brings AI-powered gold arbitrage to MaxLead',
    description:
      '0xarbitrage.org has partnered with MAXLEAD to lead its user growth strategy. To accelerate adoption, 25% of the platform’s projected inflow is allocated as a dedicated marketing budget, fully managed by MaxLead.',
    connectToWallet: true,
  },
];

export default function Page() {
  const { canUseApp, data, error: userError, setError } = useUserContext();
  const router = useRouter();
  const { disconnect: disconnectWallet } = useDisconnect();
  const { isConnected, isConnecting, isReconnecting, address } = useWalletConnection();

  const isWalletConnecting = Boolean(isConnecting || isReconnecting);
  const isDataLoading = Boolean(isConnected && data?.isLoading);
  const isLoading = isWalletConnecting || isDataLoading;

  const sanitizedUserError = useMemo(() => {
    if (!userError) return null;
    const lower = userError.toLowerCase();
    if (
      lower.includes('connector') ||
      lower.includes('@wagmi') ||
      lower.includes('viem') ||
      lower.includes('version:') ||
      lower.includes('rpc') ||
      lower.includes('internal') ||
      lower.includes('failed to authenticate')
    ) {
      return null;
    }
    return userError;
  }, [userError]);

  const isActiveInMaxleads = useMemo(() => {
    const active = data?.isActiveInMaxleads ?? data?.isActiveInMaxLeads;
    if (active !== undefined) return Boolean(active);
    return null;
  }, [data]);

  const cardState: CardState = useMemo(() => {
    if (!isConnected) return 'disconnected';
    if (sanitizedUserError) return 'error';
    if (canUseApp === false || isActiveInMaxleads === false) return 'error';
    if (canUseApp === true) return 'authenticated';
    if (isLoading || canUseApp === null) return 'loading';
    return 'loading';
  }, [isLoading, isConnected, canUseApp, sanitizedUserError, isActiveInMaxleads]);

  const errorMessage = useMemo(() => {
    if (sanitizedUserError) return sanitizedUserError;
    if (isActiveInMaxleads === false) {
      return 'Your account has been suspended by the administrator.\nPlease contact support to restore your access..';
    }
    if (canUseApp === false) {
      return 'To access this panel, please make your initial investment on the 0xArbitrage platform.';
    }
    return null;
  }, [sanitizedUserError, canUseApp, isActiveInMaxleads]);

  const handleDisconnectWallet = () => {
    setError?.(null);
    localStorage.removeItem('adminLogin');
    clearAuth();
    disconnectHandler(disconnectWallet);
  };

  const [loginInitiatedByClick, setLoginInitiatedByClick] = useState(false);

  useEffect(() => {
    if (loginInitiatedByClick && cardState === 'authenticated') {
      setLoginInitiatedByClick(false);
      router.push('/reward');
    } else if (cardState === 'error') {
      setLoginInitiatedByClick(false);
    }
  }, [loginInitiatedByClick, cardState, router]);

  useEffect(() => {
    const cardGrid = document.getElementById('card-grid');
    if (cardGrid) {
      cardGrid.onmousemove = (e) => {
        for (const card of document.getElementsByClassName('featured-card')) {
          const rect = card.getBoundingClientRect(),
            x = e.clientX - rect.left,
            y = e.clientY - rect.top;

          (card as HTMLElement).style.setProperty('--mouse-x', `${x}px`);
          (card as HTMLElement).style.setProperty('--mouse-y', `${y}px`);
        }
      };
    }
  }, []);

  return (
    <div className="fixed inset-0 h-full overflow-auto bg-black-200 flex flex-col z-10 px-4">
      <div className="container relative z-10 flex justify-between my-4">
        <div className="max-lg:hidden">
          <Link href="/" className="text-2xl font-bold text-white">
            Max-leads
          </Link>
        </div>
      </div>
      <div className="mt-16">
        <img
          className="absolute top-0 left-1/2 -translate-x-1/2 opacity-85 w-7/12"
          src="/images/gradient-blob.png"
          alt="blob"
        />
        <div className="container relative z-10 flex flex-col justify-center items-center w-full text-center">
          <h2 className="text-white font-bold text-36 lg:text-64 leading-tight mb-3">
            Backed by Visionaries.
            <br />
            Built for the Future.
          </h2>
          <p className="text-neutral-300">
            Experience a next-generation platform, shaped by innovation and
            supported by trusted global partners.
          </p>

          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 justify-center items-center mx-auto gap-5 pt-16 pb-10 group/cards mt-16"
            id="card-grid"
          >
            {items.map((item, index) =>
              item.connectToWallet ? (
                <ConnectKitButton.Custom key={index}>
                  {({ show }) => {
                    return (
                      <Card
                        key={index}
                        state={cardState}
                        errorMessage={errorMessage}
                        walletAddress={address}
                        onDisconnect={handleDisconnectWallet}
                        description={item.description}
                        title={item.title}
                        emoji={item.emoji}
                        icon={item.icon}
                        connectToWallet={true}
                        onClick={() => {
                          if (cardState === 'loading') {
                            return;
                          }
                          if (cardState === 'authenticated') {
                            router.push('/reward');
                            return;
                          }
                          if (cardState === 'error') {
                            return;
                          }
                          setLoginInitiatedByClick(true);
                          show?.();
                        }}
                      />
                    );
                  }}
                </ConnectKitButton.Custom>
              ) : (
                <Card
                  key={index}
                  title={item.title}
                  description={item.description}
                  emoji={item.emoji}
                />
              )
            )}
          </div>
        </div>
      </div>
      <footer className="flex flex-col items-center justify-center gap-8 py-10">
        <span className="text-gray-300">
          Proudly supported by Max-leads — a leader in secure blockchain
          infrastructure..
        </span>
        <span className="text-gray-300">
          © 2022 Max-leads. All Rights Reserved.
        </span>
      </footer>
    </div>
  );
}
